// What to call you, remembered between sessions.
//
// Not an account: there is no server-side identity here, and a name is a label
// on a socket rather than a claim about who you are. It lives in
// `localStorage` so the second game does not ask again, and the server clamps
// whatever arrives before anyone renders it — a name is text from a stranger.

import { MAX_NAME_LENGTH } from "src/engine/net/protocol";

const KEY = "factorychess.name";

/**
 * Names for a player who has not chosen one.
 *
 * A blank field is a worse default than a silly name: it makes naming yourself
 * feel compulsory before you can play, and it puts "Spectator" or "White" in
 * the roster where a person should be. Drawn from the game's own vocabulary,
 * so a room full of defaults still reads as this game rather than as a form.
 */
const ADJECTIVES = [
  "Brass",
  "Copper",
  "Molten",
  "Patient",
  "Restless",
  "Iron",
  "Quiet",
  "Crooked",
  "Sudden",
  "Diligent"
];

const NOUNS = [
  "Foreman",
  "Millwright",
  "Bishop",
  "Smelter",
  "Rook",
  "Conveyor",
  "Machinist",
  "Gambit",
  "Crucible",
  "Knight"
];

/** A random default. Not seeded — this is a label, not game state. */
export function suggestName(): string {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  return `${adjective} ${noun}`;
}

/**
 * The stored name, or a fresh suggestion.
 *
 * Storage can throw — Safari in private browsing, a user who has blocked it —
 * and a game that will not start because it could not remember a nickname
 * would be a poor trade. Every path here falls back to a working name.
 */
export function loadName(): string {
  try {
    const stored = window.localStorage.getItem(KEY);
    if (stored && stored.trim().length > 0) return stored.slice(0, MAX_NAME_LENGTH);
  } catch {
    // Ignore: an unavailable store means an unremembered name, nothing more.
  }
  return suggestName();
}

export function saveName(name: string): void {
  try {
    window.localStorage.setItem(KEY, name.slice(0, MAX_NAME_LENGTH));
  } catch {
    // Ignore, as above.
  }
}
